import { useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useOrderbookSender } from 'global/Datafeed/UseOrderbook/useOrderbookSender';
import { useLivePriceSender } from 'global/Datafeed/UseLivePrice/useLivePriceSender';
import { LivePriceSymbolsKeyEnum } from 'global/Datafeed/UseLivePrice/liveprice.store.types';
import { OrderbookSymbolsKeyEnum } from 'global/Datafeed/UseOrderbook/orderbook.store.types';
import { useOrderbookIEPIEV } from 'global/Datafeed/useOrderbookIEPIEV';
import {
  setValue,
  useOrderbookStore,
} from 'global/MainLayout/SideWidget/Watchlist/Orderbook/store';
import If from 'global/ErrorHandlers/If';

import TableSeparator from 'features/company/Stream/TableOrderbook/TableSeparator';
import TableBestBidOffer from 'features/company/Stream/TableOrderbook/TableBestBidOffer';
import useBestBidOffer from 'global/Datafeed/useBestBidOffer';
import useBestBidData from 'features/company/Stream/TableOrderbook/hooks/useBestBidData';
import { checkShouldTableHide } from 'features/company/Stream/TableOrderbook/utils';

import { STOCK_TYPE } from 'constants/trading';
import Summary from './Summary';
import TableBidOffer from './TableBidOffer';
import TableLivePrice from './TableLivePrice';
import { Wrapper } from './styled';
import Header from './Header';
import { effects, selectors } from './slice';

function Orderbook() {
  const dispatch = useDispatch();

  const { symbol, lastprice, percentageChange, change, ohlc, orderbook } =
    useSelector(selectors);

  const livePrice = useOrderbookStore(({ liveprice }) => liveprice?.[symbol]);
  const orderbookData = useMemo(() => {
    if (livePrice?.bid && livePrice?.offer) {
      return livePrice;
    }
    if (orderbook) {
      const liveResult = { ...orderbook };
      if (livePrice?.bid) {
        liveResult.bid = livePrice.bid;
      }
      if (livePrice?.offer) {
        liveResult.offer = livePrice.offer;
      }
      return liveResult;
    }
    return null;
  }, [orderbook, livePrice]);

  useEffect(() => {
    dispatch(effects.fetchOrderbook({ symbol }));
  }, [symbol]);

  useLivePriceSender(
    LivePriceSymbolsKeyEnum.SIDE_WIDGET_ORDERBOOK_SUMMARY,
    [symbol],
  );

  useOrderbookSender(
    OrderbookSymbolsKeyEnum.SIDE_WIDGET_ORDERBOOK_SUMMARY,
    [symbol],
  );

  // IEPIEV
  const handleMessageIepIev = (
    _symbol: string,
    value: null | {
      iep: number;
      iev: number;
    },
  ) => {
    setValue({
      key: 'liveprice',
      type: 'update_iepiev',
      symbol: _symbol,
      value,
    });
  };
  const onReceiveIEPIEV = useCallback(
    (iepiev: DatafeedIEPIEVResponse) => {
      handleMessageIepIev(symbol, {
        iep: iepiev.iep,
        iev: iepiev.iev / 100,
      });
    },
    [symbol],
  );
  useOrderbookIEPIEV([symbol], 'side_widget', onReceiveIEPIEV);

  const { time_left_seconds: iepIevTimeLeft } = orderbookData?.iepiev ?? {};

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (iepIevTimeLeft) {
      const timeoutId = setTimeout(() => {
        handleMessageIepIev(symbol, null);
      }, iepIevTimeLeft * 1000);

      return () => clearTimeout(timeoutId);
    }
  }, [symbol, iepIevTimeLeft]);
  // End IEPIEV

  // BestBidOffer
  const { iepiev } = orderbookData ?? {};
  const { showBestBid, bestBidData, isBlured, setBestBidOffer } = useBestBidData(
    iepiev?.best_bid_offer,
    symbol,
  );
  const onReceiveBestBidOffer = useCallback(
    (bestBidOffer: BestBidOfferResponse) => {
      setBestBidOffer(bestBidOffer);
    },
    [symbol],
  );
  useBestBidOffer([symbol], 'side_widget', onReceiveBestBidOffer);
  // End BestBidOffer

  const { isHideTable } = useMemo(() => {
    if (orderbookData) return checkShouldTableHide(orderbookData);
    return { isHideTable: false };
  }, [orderbookData]);

  return (
    <Wrapper>
      <Header />
      <Summary
        symbol={symbol}
        lastprice={lastprice}
        change={change}
        percentageChange={percentageChange}
      />
      <If condition={ohlc}>
        <TableLivePrice
          orderbook={ohlc}
          symbol={symbol}
          isStock={orderbook?.company_type?.toLowerCase() === STOCK_TYPE.saham}
        />
      </If>
      <If condition={showBestBid || !isHideTable}>
        <TableSeparator />
      </If>
      <If condition={orderbookData}>
        <TableBestBidOffer
          visible={showBestBid}
          previous={orderbookData?.previous}
          data={bestBidData}
        />

        <TableBidOffer
          orderbook={orderbookData}
          symbol={symbol}
          isBlur={isBlured}
        />
      </If>
    </Wrapper>
  );
}

export default Orderbook;
